import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type {
  DeviceReconciliation,
  FieldDifference,
  GatewayReconciliation,
  ReconciliationStatus,
} from "@/contracts";

import {
  featuresQuery,
  reconciliationActionMutation,
  reconciliationQuery,
  type ReconciliationCommand,
} from "@/api/queries";
import { Alert } from "@/components/ui/alert";
import { Badge, type Tone } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { ErrorBlock, LoadingBlock, PageHeader } from "@/components/ui/page";
import { DataTable, EmptyRow, Td, Th } from "@/components/ui/table";
import { useApiClient } from "@/lib/api-context";
import { EM_DASH, formatDateTime, formatDevEui } from "@/lib/format";

const STATUS_TONES: Record<ReconciliationStatus, Tone> = {
  IN_SYNC: "ok",
  DRIFT: "warn",
  UNLINKED: "info",
  ONLY_IN_ADMIN: "danger",
  ONLY_IN_NETWORK: "neutral",
};

/** Problems first, so the rows that need a decision are at the top. */
const STATUS_ORDER: Record<ReconciliationStatus, number> = {
  DRIFT: 0,
  UNLINKED: 1,
  ONLY_IN_ADMIN: 2,
  ONLY_IN_NETWORK: 3,
  IN_SYNC: 4,
};

interface PendingAction {
  command: ReconciliationCommand;
  eui: string;
  networkId: string | null;
}

/**
 * This admin against The Things Stack, side by side (ADR-089, phase 4).
 *
 * Super admin only: the application and organization on the Network Server
 * serve every tenant. Compared on opening and on demand, never in the
 * background, because each run reads every registry entry.
 */
export function NetworkSyncPage() {
  const { t } = useTranslation("pages");
  const client = useApiClient();
  const queryClient = useQueryClient();
  const features = useQuery(featuresQuery(client));
  const enabled = features.data?.networkProvisioningEnabled === true;
  const report = useQuery({ ...reconciliationQuery(client), enabled });
  const [pending, setPending] = useState<PendingAction | null>(null);

  const action = useMutation({
    ...reconciliationActionMutation(client),
    onSuccess: async () => {
      setPending(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["devices"] }),
        queryClient.invalidateQueries({ queryKey: ["gateways"] }),
      ]);
      await report.refetch();
    },
  });

  const ask = (next: PendingAction) => {
    action.reset();
    setPending(next);
  };

  return (
    <>
      <PageHeader
        title={t("networkSync.title")}
        description={t("networkSync.description")}
        action={
          <Button
            onClick={() => void report.refetch()}
            loading={report.isFetching}
            disabled={!enabled}
          >
            {t("networkSync.compareNow")}
          </Button>
        }
      />

      {features.data && !enabled ? (
        <Alert tone="info" title={t("networkSync.disabledTitle")}>
          {t("networkSync.disabledBody")}
        </Alert>
      ) : null}

      {enabled && report.isPending ? <LoadingBlock label={t("networkSync.loading")} /> : null}

      {report.isError ? (
        <ErrorBlock error={report.error} onRetry={() => void report.refetch()} />
      ) : null}

      {report.data ? (
        <>
          <p className="mb-4 text-sm text-ink-muted">
            {t("networkSync.checkedAt", {
              when: formatDateTime(report.data.checkedAt),
              application: report.data.applicationId,
              organization: report.data.organizationId,
            })}
          </p>

          <Card className="mb-6">
            <CardBody className="px-0 py-0">
              <DataTable caption={t("networkSync.devicesTitle")}>
                <thead>
                  <tr>
                    <Th>{t("networkSync.colDevEui")}</Th>
                    <Th>{t("networkSync.colOrganization")}</Th>
                    <Th>{t("networkSync.colModel")}</Th>
                    <Th>{t("networkSync.colNetworkEntry")}</Th>
                    <Th>{t("networkSync.colStatus")}</Th>
                    <Th>{t("networkSync.colDifferences")}</Th>
                    <Th>{t("networkSync.colAction")}</Th>
                  </tr>
                </thead>
                <tbody>
                  {report.data.devices.length === 0 ? (
                    <EmptyRow colSpan={7}>{t("networkSync.noDevices")}</EmptyRow>
                  ) : null}
                  {sorted(report.data.devices).map((item) => (
                    <tr key={`${item.deviceId ?? ""}:${item.networkDeviceId ?? ""}`}>
                      <Td className="font-mono text-xs">{formatDevEui(item.devEui)}</Td>
                      <Td>{item.organizationName ?? EM_DASH}</Td>
                      <Td>{item.model ?? EM_DASH}</Td>
                      <NetworkEntryCell id={item.networkDeviceId} name={item.networkName} />
                      <Td>
                        <StatusBadge status={item.status} />
                      </Td>
                      <Td>
                        <Differences differences={item.differences} />
                      </Td>
                      <Td>
                        <ActionCell item={item} onAct={() => ask(deviceAction(item))} />
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            </CardBody>
          </Card>

          <Card>
            <CardBody className="px-0 py-0">
              <DataTable caption={t("networkSync.gatewaysTitle")}>
                <thead>
                  <tr>
                    <Th>{t("networkSync.colEui")}</Th>
                    <Th>{t("networkSync.colName")}</Th>
                    <Th>{t("networkSync.colOrganization")}</Th>
                    <Th>{t("networkSync.colNetworkEntry")}</Th>
                    <Th>{t("networkSync.colStatus")}</Th>
                    <Th>{t("networkSync.colDifferences")}</Th>
                    <Th>{t("networkSync.colAction")}</Th>
                  </tr>
                </thead>
                <tbody>
                  {report.data.gateways.length === 0 ? (
                    <EmptyRow colSpan={7}>{t("networkSync.noGateways")}</EmptyRow>
                  ) : null}
                  {sorted(report.data.gateways).map((item) => (
                    <tr key={`${item.gatewayId ?? ""}:${item.networkGatewayId ?? ""}`}>
                      <Td className="font-mono text-xs">{formatDevEui(item.eui)}</Td>
                      <Td>{item.name ?? EM_DASH}</Td>
                      <Td>
                        {item.gatewayId
                          ? (item.organizationName ?? t("networkSync.shared"))
                          : EM_DASH}
                      </Td>
                      <NetworkEntryCell id={item.networkGatewayId} name={item.networkName} />
                      <Td>
                        <StatusBadge status={item.status} />
                      </Td>
                      <Td>
                        <Differences differences={item.differences} />
                      </Td>
                      <Td>
                        <ActionCell item={item} onAct={() => ask(gatewayAction(item))} />
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            </CardBody>
          </Card>
        </>
      ) : null}

      {pending ? (
        <ConfirmDialog
          open
          title={t(`networkSync.confirm.${pending.command.action}.title`)}
          confirmLabel={t(`networkSync.action.${pending.command.action}`)}
          pending={action.isPending}
          onCancel={() => setPending(null)}
          onConfirm={() => action.mutate(pending.command)}
        >
          <p>
            {t(`networkSync.confirm.${pending.command.action}.body`, {
              eui: formatDevEui(pending.eui),
              network: pending.networkId ?? EM_DASH,
            })}
          </p>
          {action.isError ? (
            <Alert className="mt-3" title={t("networkSync.actionFailed")}>
              {action.error.message}
            </Alert>
          ) : null}
        </ConfirmDialog>
      ) : null}
    </>
  );
}

function sorted<T extends { status: ReconciliationStatus }>(items: readonly T[]): T[] {
  return [...items].sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status]);
}

function deviceAction(item: DeviceReconciliation): PendingAction {
  const command: ReconciliationCommand =
    item.action === "LINK"
      ? { kind: "device", action: "LINK", id: item.deviceId!, networkId: item.networkDeviceId! }
      : { kind: "device", action: "PUSH", id: item.deviceId! };

  return { command, eui: item.devEui, networkId: item.networkDeviceId };
}

function gatewayAction(item: GatewayReconciliation): PendingAction {
  const command: ReconciliationCommand =
    item.action === "IMPORT"
      ? { kind: "gateway", action: "IMPORT", networkId: item.networkGatewayId! }
      : item.action === "LINK"
        ? {
            kind: "gateway",
            action: "LINK",
            id: item.gatewayId!,
            networkId: item.networkGatewayId!,
          }
        : {
            kind: "gateway",
            action: item.action === "REGISTER" ? "REGISTER" : "PUSH",
            id: item.gatewayId!,
          };

  return { command, eui: item.eui, networkId: item.networkGatewayId };
}

function StatusBadge({ status }: { status: ReconciliationStatus }) {
  const { t } = useTranslation("pages");

  return <Badge tone={STATUS_TONES[status]}>{t(`networkSync.status.${status}`)}</Badge>;
}

function NetworkEntryCell({ id, name }: { id: string | null; name: string | null }) {
  return (
    <Td>
      {id ? (
        <>
          <span className="block font-mono text-xs">{id}</span>
          {name ? <span className="block text-xs text-ink-muted">{name}</span> : null}
        </>
      ) : (
        EM_DASH
      )}
    </Td>
  );
}

function Differences({ differences }: { differences: readonly FieldDifference[] }) {
  const { t } = useTranslation("pages");

  if (differences.length === 0) {
    return <>{EM_DASH}</>;
  }

  return (
    <ul className="space-y-1 text-xs">
      {differences.map((difference) => (
        <li key={difference.field}>
          <span className="font-medium">{t(`networkSync.field.${difference.field}`)}</span>:{" "}
          {t("networkSync.differenceValues", {
            admin: difference.admin ?? t("networkSync.none"),
            network: difference.network ?? t("networkSync.none"),
          })}
        </li>
      ))}
    </ul>
  );
}

function ActionCell({
  item,
  onAct,
}: {
  item: DeviceReconciliation | GatewayReconciliation;
  onAct: () => void;
}) {
  const { t } = useTranslation("pages");

  if (item.action) {
    return (
      <Button size="sm" variant="secondary" onClick={onAct}>
        {t(`networkSync.action.${item.action}`)}
      </Button>
    );
  }

  return item.blockedBy ? (
    <span className="text-xs text-ink-muted">{t(`networkSync.blocked.${item.blockedBy}`)}</span>
  ) : (
    <>{EM_DASH}</>
  );
}
